import React, { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-fetching-library';
import toast from 'react-hot-toast';

import { useDidMountDebounceEffect, useDidMountEffect, useResponsive } from '../../../services/hooks';
import { fTimestamp } from '../../../utils/format-time';
import { getSaleCalendarDataAction, submitSaleCalendarDataAction } from '../../../services/request';
import { useApp } from '../../../AppContext';
import { useSelector } from 'react-redux';

function getSafeTodayWithYear(year) {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth();

  const adjusted = new Date(year, month, day);

  // Jeśli miesiąc się przesunął (czyli np. 31 czerwca -> 1 lipca), cofnij do ostatniego dnia poprzedniego miesiąca
  if (adjusted.getMonth() !== month) {
    return new Date(year, month + 1, 0); // ostatni dzień miesiąca
  }

  return adjusted;
}

const PRIORITY_GROUPS = [
  // 1 dzień – osobna hierarchia
  ['1 DZIEŃ - MUNDUROWA', '1 DZIEŃ'],

  // 3 / weekend / 5 – wspólna hierarchia
  [
    '3 DNI - MUNDUROWA',
    '3 DNI MRÓWKI',
    '3 DNI GIEWARTÓW',
    '3 DNI',
    'WEEKEND',
    'WEEKEND MRÓWKI',
    'WEEKEND GIEWARTÓW',
    '5 DNI - MUNDUROWA',
    '5 DNI MRÓWKI',
    '5 DNI GIEWARTÓW',
    '5 DNI'
  ]
];

const SaleCalendarContext = React.createContext([null, _ => {}]);

export const SaleCalendarProvider = ({ children }) => {
  const { saleCalendarViewData, setSaleCalendarViewData } = useApp();
  const { seasonYear } = useSelector(state => state.app);
  const [events, setEvents] = React.useState([]);
  const [summaryEvents, setSummaryEvents] = React.useState([]);
  const [periods, setPeriods] = React.useState([]);
  const [periodsWithSales, setPeriodsWithSales] = React.useState([]);
  const [filteredPeriodsWithSales, setFilteredPeriodsWithSales] = React.useState([]);
  const [contextMenu, setContextMenu] = React.useState({});
  const [blockedResourceDates, setBlockedResourceDates] = useState([]);
  const [customNoteEvents, setCustomNoteEvents] = useState([]);
  const [saleCalendarId, setSaleCalendarId] = React.useState();
  const [slotControlByDay, setSlotControlByDay] = React.useState([]);
  const [transitionDayEvents, setTransitionDayEvents] = React.useState([]);
  const [reserveList, setReserveList] = React.useState([]);
  const [stateColorLegendObject, setStateColorLegendObject] = React.useState({});
  const [packages, setPackages] = React.useState([]);
  const [selectedResort, setSelectedResort] = React.useState(saleCalendarViewData?.resort || 'giewartów');
  const [filters, setFilters] = useState({});

  const { loading, payload, error, query } = useQuery(getSaleCalendarDataAction(selectedResort, {}));
  const { loading: mutationLoading, mutate } = useMutation(submitSaleCalendarDataAction);

  const calendarRef = useRef(null);
  const smUp = useResponsive('up', 'sm');
  const [date, setDate] = useState(getSafeTodayWithYear(seasonYear));
  const [view, setView] = useState('resourceTimelineWeek');

  const onInitialView = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (saleCalendarViewData.view) {
        calendarApi.changeView(saleCalendarViewData.view);
        setView(saleCalendarViewData.view);
      }
      if (saleCalendarViewData.date) {
        setDate(saleCalendarViewData.date);
        calendarApi.gotoDate(saleCalendarViewData.date);
      }
    }
  }, [smUp]);

  const onChangeView = newView => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi.changeView(newView);
      setView(newView);
      setSaleCalendarViewData(prev => ({ ...prev, view: newView }));
    }
  };

  const onDateToday = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi.today();
      setDate(calendarApi.getDate());
      setSaleCalendarViewData(prev => ({ ...prev, date: calendarApi.getDate() }));
    }
  }, [calendarRef.current]);

  const onDatePrev = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi.prev();
      setDate(calendarApi.getDate());
      setSaleCalendarViewData(prev => ({ ...prev, date: calendarApi.getDate() }));
    }
  }, [calendarRef.current]);

  const onDateNext = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi.next();
      setDate(calendarApi.getDate());
      setSaleCalendarViewData(prev => ({ ...prev, date: calendarApi.getDate() }));
    }
  }, [calendarRef.current]);

  const onDateSelect = useCallback(selectedDate => {
    if (calendarRef.current && selectedDate) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi.gotoDate(selectedDate);
      setDate(selectedDate);
      setSaleCalendarViewData(prev => ({ ...prev, date: selectedDate }));
    }
  }, [calendarRef.current]);

  const onResizeEvent = useCallback(
    (
      arg,
      updateEvent
    ) => {
      const { event } = arg;

      updateEvent({
        id: event.id,
        allDay: event.allDay,
        start: fTimestamp(event.start),
        end: fTimestamp(event.end)
      });
    },
    []
  );

  const onDropEvent = useCallback(
    (
      arg,
      updateEvent
    ) => {
      const { event } = arg;

      updateEvent({
        id: event.id,
        allDay: event.allDay,
        start: fTimestamp(event.start),
        end: fTimestamp(event.end)
      });
    },
    []
  );

  const submitSaleCalendar = async (customSlotControlByDay, customReserveList) => {
    const body = {
      blocked_resource_dates: blockedResourceDates,
      notes: customNoteEvents,
      slot_control_transition_days: transitionDayEvents,
      slot_control_by_day: customSlotControlByDay || slotControlByDay,
      reserve_list: customReserveList || reserveList
    };
    const { payload, error } = await mutate(saleCalendarId, body);
    if (!error) {
      setPeriods(payload.periods);
      setEvents(payload.orders);
      setSummaryEvents(payload.summary_events);
      toast.success('Zapisano kalendarz');
    } else {
      toast.error('Nie udało się zapisać kalendarza');
    }
  };

  function getDateFromElement(el) {
    return el?.closest('[data-date]')?.getAttribute('data-date') ?? null;
  }

  function getColspanFromElement(el) {
    return el?.closest('[colspan]')?.getAttribute('colspan') ?? null;
  }

  function closeContextMenu() {
    setContextMenu({});
  }

  React.useEffect(() => {
    if (!loading && !error) {
      setSaleCalendarId(payload.id);
      setBlockedResourceDates(payload.blocked_resource_dates);
      setCustomNoteEvents(payload.notes);
      setSlotControlByDay(payload.slot_control_by_day);
      setTransitionDayEvents(payload.slot_control_transition_days);
      setPeriods(payload.periods);
      setPeriodsWithSales(payload.periods_with_sales);
      if (filters.excludedPeriods && filters.excludedPeriods.length > 0) {
        const visiblePeriods = payload.periods.filter(
          p => !filters.excludedPeriods.includes(p.id)
        );

        let filteredPeriods = [...payload.periods_with_sales];

        PRIORITY_GROUPS.forEach(group => {
          // najwyższy priorytet w grupie, który jest widoczny
          const activePeriod = [...group]
            .reverse()
            .map(title => visiblePeriods.find(p => p.title === title))
            .find(Boolean);

          if (!activePeriod) {
            // jeśli w grupie nic nie jest widoczne, nie filtrujemy tej grupy
            return;
          }

          filteredPeriods = filteredPeriods.map(p => {
            // jeśli ten rząd jest "Zostało..." i należy do tej grupy
            const parent = payload.periods.find(pp => pp.id === p.parentId);
            if (p.title === 'Zostało do sprzedania' && parent && group.includes(parent.title)) {
              // pokazujemy tylko dla aktywnego
              return p.parentId === activePeriod.id ? p : null;
            }

            return p;
          }).filter(Boolean);
        });

        // usuń wykluczone
        filteredPeriods = filteredPeriods.filter(
          p => !filters.excludedPeriods.includes(p.id)
        );

        setFilteredPeriodsWithSales(filteredPeriods);
      } else {
        let filteredPeriods = payload.periods_with_sales;

        const periodsToHide = [
          '3 DNI',
          '1 DZIEŃ - MUNDUROWA',
          '3 DNI - MUNDUROWA',
          '5 DNI - MUNDUROWA',
          'WEEKEND',
          '3 DNI GIEWARTÓW',
          '3 DNI MRÓWKI',
          'WEEKEND MRÓWKI',
          'WEEKEND GIEWARTÓW'
        ];

        const periodIds = payload.periods
          .filter(p => periodsToHide.includes(p.title))
          .map(p => p.id);

        filteredPeriods = filteredPeriods.filter(
          p => !(periodIds.includes(p.parentId) && p.title === 'Zostało do sprzedania')
        );

        setFilteredPeriodsWithSales(filteredPeriods);
      }
      setEvents(payload.orders);
      setSummaryEvents(payload.summary_events);
      setReserveList(payload.reserve_list);
      setStateColorLegendObject(payload.state_legend);
      setPackages(payload.packages);
    }
  }, [payload]);

  React.useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    const calendarEl = calendarApi?.el;

    if (!calendarEl) {
      return;
    }

    const handleContextMenu = e => {
      e.preventDefault();
      const target = e.target;
      const slotEl = target.closest('.fc-timeline-slot, .fc-resource');
      if (!slotEl) {
        return;
      }
      let dateAttr = slotEl?.dataset?.date;
      let resourceId = slotEl?.dataset?.resourceId;
      if (resourceId && (resourceId.includes('-sm') || resourceId.includes('-zds'))) {
        // Dla eventów z podsumowaniem sprzedązy/miejsc nie pokazujemy contextmenu
        return;
      }
      let contextType = 'periodContextMenu';

      if (slotEl.getAttribute('class').includes('fc-timeline-lane')) {
        contextType = 'dayContextMenu';
      }

      if ((!dateAttr && !resourceId) || (!dateAttr && contextType == 'dayContextMenu')) {
        dateAttr = target.fcSeg?.start.toISOString().split('T')[0];
      };
      // Jeśli resourceId nie znaleziony bezpośrednio, znajdź po pozycji Y kursora
      if (!resourceId) {
        const mouseY = e.clientY;
        const rows = Array.from(document.querySelectorAll('.fc-datagrid-body tr'));
        for (const row of rows) {
          const rect = row.getBoundingClientRect();
          if (mouseY >= rect.top && mouseY <= rect.bottom) {
            const td = row.querySelector('[data-resource-id]');
            if (td) {
              resourceId = td.getAttribute('data-resource-id');
              contextType = 'dayContextMenu';
            }
            break;
          }
        }
      }
      const colspan = getColspanFromElement(target);
      if (colspan) {
        contextType = 'dayContextMenu';
      };
      if (dateAttr || resourceId) {
        if (contextType == 'dayContextMenu' && !colspan) {
          return;
        }
        const start = calendarApi.currentData.dateProfile.currentRange.start;
        const end = calendarApi.currentData.dateProfile.currentRange.end;
        setContextMenu({
          visible: true,
          x: e.pageX,
          y: e.pageY,
          date: dateAttr ?? getDateFromElement(target),
          resourceId,
          colspan,
          start,
          end,
          contextType
        });
      } else {
        closeContextMenu();
      }
    };

    const handleClickOutside = e => {
      const modalEl = document.querySelector('#block-modal');
      const contextMenuEl = document.querySelector('[role="menu"]');
      if (
        (modalEl && modalEl.contains(e.target)) ||
        (contextMenuEl && contextMenuEl.contains(e.target))
      ) {
        return;
      }
      closeContextMenu();
    };

    calendarEl.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      calendarEl.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  useDidMountDebounceEffect(() => {
    query(selectedResort, {});
  }, [selectedResort], 1000);

  useDidMountEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    const newDate = getSafeTodayWithYear(seasonYear);
    setDate(newDate);
    calendarApi.gotoDate(newDate);

  }, [seasonYear]);

  useDidMountEffect(() => {
    if (!filters.excludedPeriods) {
      return;
    }

    let filteredPeriods = periodsWithSales;

    const threeDayTitles = [
      '3 DNI',
      '3 DNI - MUNDUROWA',
      '3 DNI GIEWARTÓW',
      '3 DNI MRÓWKI'
    ];

    const fiveDayTitles = [
      '5 DNI',
      '5 DNI - MUNDUROWA',
      '5 DNI GIEWARTÓW',
      '5 DNI MRÓWKI'
    ];

    const oneDayTitles = [
      '1 DZIEŃ - MUNDUROWA',
      '1 DZIEŃ'
    ];

    const weekendTitles = [
      'WEEKEND',
      'WEEKEND MRÓWKI',
      'WEEKEND GIEWARTÓW'
    ];

    const isAnyFiveDayVisible = periods.some(
      p => fiveDayTitles.includes(p.title) &&
        !filters.excludedPeriods.includes(p.id)
    );

    const visibleFiveDayPeriods = periods.filter(
      p => fiveDayTitles.includes(p.title) &&
        !filters.excludedPeriods.includes(p.id)
    );

    // jeśli widocznych jest kilka 5-dniówek,
    // "Zostało..." zostaje tylko przy OSTATNIEJ
    if (visibleFiveDayPeriods.length > 1) {
      const lastFiveDayId =
        visibleFiveDayPeriods[visibleFiveDayPeriods.length - 1].id;

      filteredPeriods = filteredPeriods.filter(
        p => !(
          fiveDayTitles.includes(
            periods.find(pp => pp.id === p.parentId)?.title ?? ''
          ) &&
            p.title === 'Zostało do sprzedania' &&
            p.parentId !== lastFiveDayId
        )
      );
    }

    const visibleThreeDayPeriods = periods.filter(
      p => threeDayTitles.includes(p.title) &&
        !filters.excludedPeriods.includes(p.id)
    );

    // jeśli widocznych jest kilka 3-dniówek,
    // "Zostało..." zostaje tylko przy OSTATNIEJ
    if (visibleThreeDayPeriods.length > 1) {
      const lastThreeDayId =
        visibleThreeDayPeriods[visibleThreeDayPeriods.length - 1].id;

      filteredPeriods = filteredPeriods.filter(
        p => !(
          threeDayTitles.includes(
            periods.find(pp => pp.id === p.parentId)?.title ?? ''
          ) &&
            p.title === 'Zostało do sprzedania' &&
            p.parentId !== lastThreeDayId
        )
      );
    }

    const visibleOneDayPeriods = periods.filter(
      p => oneDayTitles.includes(p.title) &&
        !filters.excludedPeriods.includes(p.id)
    );

    // jeśli widoczne są obie 1-dniówki,
    // "Zostało..." zostaje tylko przy OSTATNIEJ (czyli 1 DZIEŃ)
    if (visibleOneDayPeriods.length > 1) {
      const lastOneDayId =
        visibleOneDayPeriods[visibleOneDayPeriods.length - 1].id;

      filteredPeriods = filteredPeriods.filter(
        p => !(
          oneDayTitles.includes(
            periods.find(pp => pp.id === p.parentId)?.title ?? ''
          ) &&
            p.title === 'Zostało do sprzedania' &&
            p.parentId !== lastOneDayId
        )
      );
    }

    const isWeekendVisible = periods.some(
      p => weekendTitles.includes(p.title) &&
        !filters.excludedPeriods.includes(p.id)
    );

    const threeDayPeriods = periods.filter(
      p => threeDayTitles.includes(p.title)
    );

    const weekendPeriods = periods.filter(
      p => weekendTitles.includes(p.title)
    );

    // 3 DNI → chowamy "Zostało do sprzedania",
    // jeśli cokolwiek dłuższego jest widoczne
    if (
      (isAnyFiveDayVisible || isWeekendVisible) &&
      threeDayPeriods.length
    ) {
      const threeDayIds = threeDayPeriods.map(p => p.id);

      filteredPeriods = filteredPeriods.filter(
        p => !(
          threeDayIds.includes(p.parentId) &&
            p.title === 'Zostało do sprzedania'
        )
      );
    }

    // WEEKEND → chowamy "Zostało do sprzedania",
    // jeśli widoczna jest jakakolwiek 5-dniówka
    if (isAnyFiveDayVisible && weekendPeriods.length) {
      const weekendIds = weekendPeriods.map(p => p.id);

      filteredPeriods = filteredPeriods.filter(
        p => !(
          weekendIds.includes(p.parentId) &&
            p.title === 'Zostało do sprzedania'
        )
      );
    }

    setFilteredPeriodsWithSales(
      filteredPeriods.filter(
        p => !filters.excludedPeriods.includes(p.id)
      )
    );
  }, [filters]);

  return (
    <SaleCalendarContext.Provider value={
      { events,
        setEvents,
        summaryEvents,
        setSummaryEvents,
        periods,
        setPeriods,
        periodsWithSales: filteredPeriodsWithSales,
        isSaving: mutationLoading,
        loading,
        calendarRef,
        contextMenu,
        setContextMenu,
        blockedResourceDates,
        setBlockedResourceDates,
        customNoteEvents,
        setCustomNoteEvents,
        transitionDayEvents,
        setTransitionDayEvents,
        slotControlByDay,
        setSlotControlByDay,
        reserveList,
        setReserveList,
        selectedResort,
        setSelectedResort,
        saleCalendarId,
        setSaleCalendarId,
        stateColorLegendObject,
        packages,
        filters,
        setFilters,
        date,
        //
        view,
        date,
        //
        onDatePrev,
        onDateNext,
        onDateToday,
        onDateSelect,
        onDropEvent,
        onChangeView,
        onResizeEvent,
        onInitialView,
        closeContextMenu,
        submitSaleCalendar,
        queryCalendar: query }}>
      {children}
    </SaleCalendarContext.Provider>
  );
};

export default SaleCalendarContext;

export const useSaleCalendar = () => React.useContext(SaleCalendarContext);
